import type {Evaluator} from "@cm/graph-v3"
import {z} from "zod/v4-mini"
import {outputKeyFromOutputTypeAndOutputId, skipped, visitNone} from "@cm/template-nodes"
import type {ExternalId, OnCompileContext, OnFilterActiveContext, TemplateNodeContext, VisitorNodeResult} from "@cm/template-nodes"
import {MattressNode} from "#mattress/nodes/mattress-node"
import type {MattressCurveLikeNode, MattressReferenceFrameLikeNode} from "#mattress/role-markers"
import type {EvaluatedMattressComponentValueType} from "#mattress/nodes/component/mattress-component-types"
import type {MattressComponentInterfaceDescriptor} from "#mattress/nodes/component/mattress-component-interface-descriptors"
import {getMattressCompileContext} from "#mattress/nodes/component/mattress-component-helpers"

export type MattressExportNodeType = MattressCurveLikeNode | MattressReferenceFrameLikeNode

export type MattressExportParameters<NodeType extends MattressExportNodeType = MattressExportNodeType> = {
    name: string
    id: ExternalId
    node: NodeType | null
}

type MattressInfoClassCtor = new (...args: any[]) => MattressComponentInterfaceDescriptor<unknown>

export abstract class MattressExport<NodeType extends MattressExportNodeType = MattressExportNodeType> extends MattressNode<
    MattressExportParameters<NodeType>
> {
    protected abstract get outputType(): EvaluatedMattressComponentValueType
    protected abstract get InfoClass(): MattressInfoClassCtor

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<MattressExportParameters<NodeType>> {
        if (this.parameters.node === null) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MattressExportParameters<NodeType>> {
        const {id, name, node} = this.parameters
        if (node === null) return skipped

        const mattressContext = getMattressCompileContext(context)
        const {evaluator} = context
        const {componentOutputs, componentsDescriptorLists} = mattressContext.evaluatedData

        const scope = evaluator.getScope(this)
        const nodeData = evaluator.evaluate(node)

        componentOutputs[outputKeyFromOutputTypeAndOutputId(this.outputType, id)] = nodeData

        const InfoClass = this.InfoClass
        componentsDescriptorLists.push(
            scope.pureLambda(
                scope.tuple(id, name, nodeData, this, InfoClass),
                ([id, name, value, origin, InfoClass]) => [new InfoClass({id, name, value, type: "output" as const, origin})],
                "mattressExportInfo",
            ),
        )

        return visitNone(this.parameters)
    }
}

export const mattressExport = z.custom<MattressExport>((x) => x instanceof MattressExport)
